import React, { useEffect, useState } from 'react';
import { ENV } from '../../config/env';

interface Insight {
  id: string;
  insight_type: 'warning' | 'alert' | 'opportunity' | 'success';
  title: string;
  description: string;
  impact: string;
  suggested_action: string;
  priority: 'high' | 'medium' | 'low';
  created_at: string;
}

interface ProactiveInsightsProps {
  organizationId?: string;
  onInsightClick?: (insight: Insight) => void;
}

const ProactiveInsights: React.FC<ProactiveInsightsProps> = ({
  organizationId,
  onInsightClick
}) => {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadInsights();
  }, [organizationId]);

  const loadInsights = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const url = organizationId
        ? `${ENV.API_URL}/api/business-consultant/insights?organization_id=${organizationId}`
        : `${ENV.API_URL}/api/business-consultant/insights`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch insights');
      }

      const data = await response.json();
      setInsights(data.insights || []);
    } catch (err: any) {
      console.error('Error loading insights:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (insightId: string) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${ENV.API_URL}/api/business-consultant/insights/${insightId}/read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setInsights(insights.filter(i => i.id !== insightId));
    } catch (err) {
      console.error('Error marking insight as read:', err);
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'alert': return '🚨';
      case 'warning': return '⚠️';
      case 'opportunity': return '💡';
      case 'success': return '🎉';
      default: return '📊';
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'alert': return 'bg-red-50 border-red-200';
      case 'warning': return 'bg-yellow-50 border-yellow-200';
      case 'opportunity': return 'bg-blue-50 border-blue-200';
      case 'success': return 'bg-green-50 border-green-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getPriorityBadge = (priority: string) => {
    const colors = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-gray-100 text-gray-800'
    };
    return colors[priority as keyof typeof colors] || colors.low;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        Error loading insights: {error}
      </div>
    );
  }

  if (insights.length === 0) {
    return (
      <div className="p-6 bg-gradient-to-br from-green-50 to-blue-50 border border-green-200 rounded-lg text-center">
        <div className="text-4xl mb-2">✅</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">All Clear!</h3>
        <p className="text-sm text-gray-600">
          No urgent items need your attention right now. Check back tomorrow for your daily business analysis.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">
          📊 Daily Business Insights ({insights.length})
        </h3>
        <button
          onClick={loadInsights}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
        >
          Refresh
        </button>
      </div>

      {insights.map((insight) => (
        <div
          key={insight.id}
          className={`p-4 border rounded-lg ${getInsightColor(insight.insight_type)} transition-all hover:shadow-md cursor-pointer`}
          onClick={() => onInsightClick?.(insight)}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xl">{getInsightIcon(insight.insight_type)}</span>
              <h4 className="font-semibold text-gray-900 text-sm">{insight.title}</h4>
            </div>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityBadge(insight.priority)}`}>
              {insight.priority}
            </span>
          </div>

          <p className="text-sm text-gray-700 mb-2">{insight.description}</p>

          <div className="bg-white bg-opacity-50 rounded p-2 mb-2">
            <p className="text-xs font-medium text-gray-600 mb-1">Impact:</p>
            <p className="text-sm text-gray-800">{insight.impact}</p>
          </div>

          <div className="bg-white bg-opacity-50 rounded p-2">
            <p className="text-xs font-medium text-gray-600 mb-1">Suggested Action:</p>
            <p className="text-sm text-gray-800">{insight.suggested_action}</p>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {new Date(insight.created_at).toLocaleDateString()}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                markAsRead(insight.id);
              }}
              className="text-xs text-gray-600 hover:text-gray-800 font-medium"
            >
              Mark as read ✓
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProactiveInsights;
